import type { AxiosError } from 'axios';
import type { AuthError, PostgrestError } from '@supabase/supabase-js';

import { logger } from '@/services/logger';

import type {
  TApiErrorDetails,
  TApiErrorResponse,
  TNetworkError,
  TThrownError,
} from './errors.types';
import { ERROR_CODES, ERROR_TYPE } from './errors.types';

const UNKNOWN_WHERE = 'unknown';
const NETWORK_CODE = ERROR_CODES.network;

const ACCESS_DENIED_CODES = ['403', '42501', 'PGRST301', ERROR_CODES.forbidden];
const NOT_FOUND_CODES = ['404', 'PGRST116'];

const createNetworkError = (
  code: string,
  message?: string,
  where: string = UNKNOWN_WHERE,
  reason?: string,
): TNetworkError => {
  let finalMessage = message ?? 'Unknown error';

  if (!message && NOT_FOUND_CODES.includes(code)) {
    finalMessage = 'Not found';
  }

  if (!message && ACCESS_DENIED_CODES.includes(code)) {
    finalMessage = 'Access denied';
  }

  if (!message && code === NETWORK_CODE) {
    finalMessage = 'No connection';
  }

  const error: TNetworkError = {
    type: ERROR_TYPE,
    code,
    message: finalMessage,
    where,
    reason,
  };

  logger.error(`${where} — ${code}: ${finalMessage}`);

  return error;
};

const isNotFoundCode = (code?: string | null): boolean => NOT_FOUND_CODES.includes(code ?? '');

const isNetworkError = (instance: unknown): instance is TNetworkError => {
  return (instance as TNetworkError)?.type === ERROR_TYPE;
};

const readReason = (details: unknown): string | undefined => {
  const reason = (details as TApiErrorDetails | undefined)?.reason;

  if (typeof reason !== 'string') {
    return undefined;
  }

  return reason;
};

const fromAxios = (error: AxiosError<TApiErrorResponse>, where: string): TNetworkError => {
  const status = error.response?.status;

  if (status === undefined) {
    return createNetworkError(NETWORK_CODE, undefined, where);
  }

  const payload = error.response?.data;

  return createNetworkError(payload?.error ?? String(status), payload?.message, where, readReason(payload?.details));
};

const fromSupabaseAuth = (error: AuthError, where: string): TNetworkError => {
  const code = error.code ?? error.status?.toString() ?? ERROR_CODES.unknown;

  return createNetworkError(code, error.message, where);
};

const REASON_SHAPE = /^[a-z][a-z_]*$/;

const readPostgrestReason = (details: string | null): string | undefined => {
  if (!details || !REASON_SHAPE.test(details)) {
    return undefined;
  }

  return details;
};

const fromPostgrest = (error: PostgrestError, where: string): TNetworkError => {
  const code = error.code || ERROR_CODES.unknown;
  const isMapped = ACCESS_DENIED_CODES.includes(code) || NOT_FOUND_CODES.includes(code);

  if (isMapped) {
    logger.warn(`${where} — ${code}: ${error.message}`, error.details);

    return createNetworkError(code, undefined, where);
  }

  return createNetworkError(code, error.message, where, readPostgrestReason(error.details));
};

const toThrowable = (error: TNetworkError): TThrownError => {
  return Object.assign(new Error(error.code), { where: error.where, detail: error.message, reason: error.reason });
};

const unwrap = <TResponse>(result: TResponse | TNetworkError): TResponse => {
  if (isNetworkError(result)) {
    throw toThrowable(result);
  }

  return result;
};

const errorUtils = {
  createNetworkError,
  isNetworkError,
  isNotFoundCode,
  fromAxios,
  fromPostgrest,
  fromSupabaseAuth,
  toThrowable,
  unwrap,
};

export {
  errorUtils,
};
